import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, History, Pencil, Plus, Trash2 } from 'lucide-react';

import { listIssues } from '@/api/eos/issues.api';
import { listRocks } from '@/api/eos/rocks.api';
import {
  EMPTY_VTO_DATA,
  getVto,
  getVtoVersion,
  normalizeVtoData,
  saveVto,
  type VtoData,
  type VtoHistoryEntry,
} from '@/api/eos/vto.api';
import { RockStatusBadge } from '@/components/eos/RockStatusBadge';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { describeEosError } from '@/lib/eos/errors';
import { quarterLabel, quarterStartOf, toIsoDate } from '@/lib/eos/quarters';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * EOS V/TO — Stream B Wave 3 (ADR-023).
 *
 * The classic two-page Vision/Traction Organizer: VISION column (core
 * values → core focus → 10-year target → marketing strategy) and
 * TRACTION column (3-year picture → 1-year plan → rocks → issues).
 * Reads are org-wide; edits are gated on `manage_eos` (superadmins
 * implicit) and every save appends an immutable version. Rocks and
 * long-term issues render LIVE from their own modules — they are not
 * part of the stored document.
 */

const INPUT_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

type SectionKey =
  | 'coreValues'
  | 'coreFocus'
  | 'tenYearTarget'
  | 'marketingStrategy'
  | 'threeYearPicture'
  | 'oneYearPlan';

function versionOptionLabel(entry: VtoHistoryEntry): string {
  const date = new Date(entry.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const summary = entry.changeSummary ? ` — ${entry.changeSummary}` : '';
  return `v${String(entry.version)} · ${date}${summary}`;
}

export function VtoPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const canEdit = me !== null && (isSuperadmin(me) || hasCapability(me, 'manage_eos'));

  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);
  const [draft, setDraft] = useState<VtoData | null>(null);
  const [editing, setEditing] = useState<ReadonlySet<SectionKey>>(new Set());
  const [saveOpen, setSaveOpen] = useState(false);

  const vtoQuery = useQuery({ queryKey: ['eos', 'vto'], queryFn: () => getVto() });

  const versionQuery = useQuery({
    queryKey: ['eos', 'vto', 'version', viewingVersionId],
    queryFn: () => getVtoVersion(viewingVersionId ?? ''),
    enabled: viewingVersionId !== null,
  });

  const currentQuarter = useMemo(() => toIsoDate(quarterStartOf(new Date())), []);
  const rocksQuery = useQuery({
    queryKey: ['eos', 'rocks', currentQuarter, 'company'],
    queryFn: () => listRocks({ quarterStart: currentQuarter, type: 'company' }),
  });
  const issuesQuery = useQuery({
    queryKey: ['eos', 'issues', 'long_term'],
    queryFn: () => listIssues({ term: 'long_term' }),
  });

  const current = vtoQuery.data?.current ?? null;
  const history = vtoQuery.data?.history ?? [];

  const viewingHistory = viewingVersionId !== null && viewingVersionId !== current?.id;

  // The document being rendered: past version > unsaved draft > current > empty skeleton.
  const base = useMemo<VtoData>(() => {
    if (viewingHistory) {
      return versionQuery.data ? normalizeVtoData(versionQuery.data.dataJson) : EMPTY_VTO_DATA;
    }
    return current ? normalizeVtoData(current.dataJson) : EMPTY_VTO_DATA;
  }, [viewingHistory, versionQuery.data, current]);

  const data = viewingHistory ? base : (draft ?? base);

  const longTermIssues = useMemo(
    () =>
      (issuesQuery.data ?? []).filter(
        (i) => i.archivedAt === null && (i.status === 'identified' || i.status === 'discussing'),
      ),
    [issuesQuery.data],
  );

  const saveMutation = useMutation({
    mutationFn: (changeSummary: string) =>
      saveVto({
        dataJson: draft ?? base,
        changeSummary: changeSummary.trim() || undefined,
      }),
    onSuccess: () => {
      setSaveOpen(false);
      setDraft(null);
      setEditing(new Set());
      void queryClient.invalidateQueries({ queryKey: ['eos', 'vto'] });
      toast.show('V/TO saved — a new version was created.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not save the V/TO.'), { variant: 'danger' });
    },
  });

  function beginEdit(section: SectionKey): void {
    if (!canEdit || viewingHistory) return;
    setDraft((prev) => prev ?? base);
    setEditing((prev) => new Set(prev).add(section));
  }

  function patchDraft(fn: (d: VtoData) => VtoData): void {
    setDraft((prev) => fn(prev ?? base));
  }

  function discard(): void {
    setDraft(null);
    setEditing(new Set());
  }

  if (vtoQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (vtoQuery.error) {
    return (
      <Card className="mx-auto flex max-w-2xl items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div>
          <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load the V/TO</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {vtoQuery.error instanceof Error ? vtoQuery.error.message : 'Unknown error'}
          </p>
        </div>
      </Card>
    );
  }

  const sectionProps = (key: SectionKey) => ({
    editing: !viewingHistory && editing.has(key),
    canEdit: canEdit && !viewingHistory,
    onEdit: () => {
      beginEdit(key);
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Vision/Traction Organizer</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            The company V/TO — everyone sees the vision; edits create a new version.
          </p>
        </div>
        {history.length > 0 ? (
          <label className="flex items-center gap-2">
            <History className="size-4 text-kw-muted" />
            <select
              aria-label="Version history"
              value={viewingHistory ? viewingVersionId : ''}
              disabled={draft !== null}
              onChange={(e) => {
                setViewingVersionId(e.target.value || null);
              }}
              className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20 disabled:bg-kw-bg disabled:text-kw-muted"
            >
              <option value="">{current ? `Current (v${String(current.version)})` : 'Current'}</option>
              {history
                .filter((h) => h.id !== current?.id)
                .map((h) => (
                  <option key={h.id} value={h.id}>
                    {versionOptionLabel(h)}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
      </div>

      {viewingHistory ? (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-kw-cerulean/40 bg-kw-cerulean-light p-4">
          <p className="text-[13px] text-kw-cerulean-dark">
            {versionQuery.isLoading
              ? 'Loading version…'
              : versionQuery.data
                ? `Viewing version ${String(versionQuery.data.version)} (read-only) — saved ${new Date(versionQuery.data.createdAt).toLocaleString()}.`
                : 'This version could not be loaded.'}
          </p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setViewingVersionId(null);
            }}
          >
            Back to current
          </Button>
        </Card>
      ) : null}

      {draft !== null ? (
        <Card className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 border-kw-warning-text/30 bg-kw-warning-bg p-4">
          <p className="text-[13px] font-medium text-kw-warning-text">
            Unsaved V/TO edits — saving creates a new version.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={discard}>
              Discard
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setSaveOpen(true);
              }}
            >
              Save V/TO
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* ── VISION ─────────────────────────────────────────────────── */}
        <div className="space-y-4">
          <h2 className="text-[13px] font-semibold uppercase tracking-wide text-kw-muted">Vision</h2>

          <SectionCard title="Core Values" {...sectionProps('coreValues')}>
            {editing.has('coreValues') && !viewingHistory ? (
              <StringListEditor
                items={data.coreValues}
                max={10}
                placeholder="e.g. Do the right thing"
                ariaLabel="Core value"
                onChange={(items) => {
                  patchDraft((d) => ({ ...d, coreValues: items }));
                }}
              />
            ) : (
              <BulletList
                items={data.coreValues}
                placeholder="No core values yet. Define 3–7 timeless behaviors your best people already live."
              />
            )}
          </SectionCard>

          <SectionCard title="Core Focus" {...sectionProps('coreFocus')}>
            <div className="space-y-3">
              <Field label="Purpose / Cause / Passion">
                {editing.has('coreFocus') && !viewingHistory ? (
                  <textarea
                    aria-label="Purpose"
                    value={data.coreFocus.purpose}
                    maxLength={300}
                    rows={2}
                    onChange={(e) => {
                      patchDraft((d) => ({ ...d, coreFocus: { ...d.coreFocus, purpose: e.target.value } }));
                    }}
                    className={INPUT_CLS}
                  />
                ) : (
                  <TextBlock value={data.coreFocus.purpose} placeholder="Why does the company exist?" />
                )}
              </Field>
              <Field label="Niche">
                {editing.has('coreFocus') && !viewingHistory ? (
                  <textarea
                    aria-label="Niche"
                    value={data.coreFocus.niche}
                    maxLength={300}
                    rows={2}
                    onChange={(e) => {
                      patchDraft((d) => ({ ...d, coreFocus: { ...d.coreFocus, niche: e.target.value } }));
                    }}
                    className={INPUT_CLS}
                  />
                ) : (
                  <TextBlock value={data.coreFocus.niche} placeholder="What is the company best at?" />
                )}
              </Field>
            </div>
          </SectionCard>

          <SectionCard title="10-Year Target" {...sectionProps('tenYearTarget')}>
            {editing.has('tenYearTarget') && !viewingHistory ? (
              <textarea
                aria-label="10-Year Target"
                value={data.tenYearTarget}
                maxLength={300}
                rows={2}
                onChange={(e) => {
                  patchDraft((d) => ({ ...d, tenYearTarget: e.target.value }));
                }}
                className={INPUT_CLS}
              />
            ) : (
              <TextBlock
                value={data.tenYearTarget}
                placeholder="Where is the company going? One big, energizing 10-year goal."
              />
            )}
          </SectionCard>

          <SectionCard title="Marketing Strategy" {...sectionProps('marketingStrategy')}>
            <div className="space-y-3">
              <Field label="Target Market — “The List”">
                {editing.has('marketingStrategy') && !viewingHistory ? (
                  <textarea
                    aria-label="Target market"
                    value={data.marketingStrategy.targetMarket}
                    maxLength={300}
                    rows={2}
                    onChange={(e) => {
                      patchDraft((d) => ({
                        ...d,
                        marketingStrategy: { ...d.marketingStrategy, targetMarket: e.target.value },
                      }));
                    }}
                    className={INPUT_CLS}
                  />
                ) : (
                  <TextBlock
                    value={data.marketingStrategy.targetMarket}
                    placeholder="Who is the ideal customer?"
                  />
                )}
              </Field>
              <Field label="Three Uniques">
                {editing.has('marketingStrategy') && !viewingHistory ? (
                  <StringListEditor
                    items={data.marketingStrategy.threeUniques}
                    max={3}
                    placeholder="What sets us apart?"
                    ariaLabel="Unique"
                    onChange={(items) => {
                      patchDraft((d) => ({
                        ...d,
                        marketingStrategy: { ...d.marketingStrategy, threeUniques: items },
                      }));
                    }}
                  />
                ) : (
                  <BulletList
                    items={data.marketingStrategy.threeUniques}
                    placeholder="The three things that set the company apart."
                  />
                )}
              </Field>
              <Field label="Proven Process">
                {editing.has('marketingStrategy') && !viewingHistory ? (
                  <textarea
                    aria-label="Proven process"
                    value={data.marketingStrategy.provenProcess}
                    maxLength={300}
                    rows={2}
                    onChange={(e) => {
                      patchDraft((d) => ({
                        ...d,
                        marketingStrategy: { ...d.marketingStrategy, provenProcess: e.target.value },
                      }));
                    }}
                    className={INPUT_CLS}
                  />
                ) : (
                  <TextBlock
                    value={data.marketingStrategy.provenProcess}
                    placeholder="The named, repeatable way you deliver."
                  />
                )}
              </Field>
              <Field label="Guarantee">
                {editing.has('marketingStrategy') && !viewingHistory ? (
                  <textarea
                    aria-label="Guarantee"
                    value={data.marketingStrategy.guarantee}
                    maxLength={300}
                    rows={2}
                    onChange={(e) => {
                      patchDraft((d) => ({
                        ...d,
                        marketingStrategy: { ...d.marketingStrategy, guarantee: e.target.value },
                      }));
                    }}
                    className={INPUT_CLS}
                  />
                ) : (
                  <TextBlock
                    value={data.marketingStrategy.guarantee}
                    placeholder="The promise that removes the customer's biggest fear."
                  />
                )}
              </Field>
            </div>
          </SectionCard>
        </div>

        {/* ── TRACTION ───────────────────────────────────────────────── */}
        <div className="space-y-4">
          <h2 className="text-[13px] font-semibold uppercase tracking-wide text-kw-muted">Traction</h2>

          <SectionCard title="3-Year Picture" {...sectionProps('threeYearPicture')}>
            <PlanFields
              editing={editing.has('threeYearPicture') && !viewingHistory}
              futureDate={data.threeYearPicture.futureDate}
              revenue={data.threeYearPicture.revenue}
              profit={data.threeYearPicture.profit}
              onChange={(patch) => {
                patchDraft((d) => ({ ...d, threeYearPicture: { ...d.threeYearPicture, ...patch } }));
              }}
            />
            <div className="mt-3 space-y-3">
              <Field label="Measurables">
                {editing.has('threeYearPicture') && !viewingHistory ? (
                  <StringListEditor
                    items={data.threeYearPicture.measurables}
                    max={10}
                    placeholder="e.g. 40 team members"
                    ariaLabel="Measurable"
                    onChange={(items) => {
                      patchDraft((d) => ({
                        ...d,
                        threeYearPicture: { ...d.threeYearPicture, measurables: items },
                      }));
                    }}
                  />
                ) : (
                  <BulletList
                    items={data.threeYearPicture.measurables}
                    placeholder="Key numbers that prove the picture came true."
                  />
                )}
              </Field>
              <Field label="What does it look like?">
                {editing.has('threeYearPicture') && !viewingHistory ? (
                  <StringListEditor
                    items={data.threeYearPicture.bullets}
                    max={15}
                    placeholder="Paint the picture, one bullet at a time"
                    ariaLabel="Picture bullet"
                    onChange={(items) => {
                      patchDraft((d) => ({
                        ...d,
                        threeYearPicture: { ...d.threeYearPicture, bullets: items },
                      }));
                    }}
                  />
                ) : (
                  <BulletList
                    items={data.threeYearPicture.bullets}
                    placeholder="Bullets that let everyone see the same 3-year future."
                  />
                )}
              </Field>
            </div>
          </SectionCard>

          <SectionCard title="1-Year Plan" {...sectionProps('oneYearPlan')}>
            <PlanFields
              editing={editing.has('oneYearPlan') && !viewingHistory}
              futureDate={data.oneYearPlan.futureDate}
              revenue={data.oneYearPlan.revenue}
              profit={data.oneYearPlan.profit}
              onChange={(patch) => {
                patchDraft((d) => ({ ...d, oneYearPlan: { ...d.oneYearPlan, ...patch } }));
              }}
            />
            <div className="mt-3">
              <Field label="Goals">
                {editing.has('oneYearPlan') && !viewingHistory ? (
                  <StringListEditor
                    items={data.oneYearPlan.goals}
                    max={10}
                    placeholder="e.g. Launch the new fulfillment center"
                    ariaLabel="Goal"
                    onChange={(items) => {
                      patchDraft((d) => ({ ...d, oneYearPlan: { ...d.oneYearPlan, goals: items } }));
                    }}
                  />
                ) : (
                  <BulletList
                    items={data.oneYearPlan.goals}
                    placeholder="3–7 goals for the year. Less is more."
                  />
                )}
              </Field>
            </div>
          </SectionCard>

          {/* Live from the Rocks module — not part of the stored document. */}
          <SectionCard
            title={`Rocks — ${quarterLabel(quarterStartOf(new Date()))}`}
            editing={false}
            canEdit={false}
            onEdit={() => undefined}
          >
            {rocksQuery.isLoading ? (
              <p className="text-[13px] text-kw-muted">Loading rocks…</p>
            ) : (rocksQuery.data ?? []).length === 0 ? (
              <p className="text-[13px] italic text-kw-muted">
                No company rocks this quarter — set them on the Rocks page.
              </p>
            ) : (
              <ul className="space-y-2">
                {(rocksQuery.data ?? []).map((rock) => (
                  <li key={rock.id} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-kw-shark">{rock.title}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      {rock.ownerMembership ? (
                        <span className="text-[12px] text-kw-muted">
                          {rock.ownerMembership.user.firstName} {rock.ownerMembership.user.lastName}
                        </span>
                      ) : null}
                      <RockStatusBadge status={rock.status} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {/* Live from the Issues module — not part of the stored document. */}
          <SectionCard title="Long-Term Issues" editing={false} canEdit={false} onEdit={() => undefined}>
            {issuesQuery.isLoading ? (
              <p className="text-[13px] text-kw-muted">Loading issues…</p>
            ) : longTermIssues.length === 0 ? (
              <p className="text-[13px] italic text-kw-muted">
                No long-term issues parked — raise them on the Issues page.
              </p>
            ) : (
              <ul className="list-inside list-disc space-y-1.5">
                {longTermIssues.map((issue) => (
                  <li key={issue.id} className="text-[13px] text-kw-shark">
                    {issue.title}
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>

      <Modal
        open={saveOpen}
        onClose={() => {
          setSaveOpen(false);
        }}
        title="Save V/TO"
        size="sm"
      >
        <SaveVtoForm
          isSaving={saveMutation.isPending}
          error={saveMutation.error}
          onCancel={() => {
            setSaveOpen(false);
          }}
          onSave={(summary) => {
            saveMutation.mutate(summary);
          }}
        />
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Section building blocks
// ─────────────────────────────────────────────────────────────────────────

function SectionCard({
  title,
  editing,
  canEdit,
  onEdit,
  children,
}: {
  title: string;
  editing: boolean;
  canEdit: boolean;
  onEdit: () => void;
  children: ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold text-kw-shark">{title}</h3>
        {canEdit && !editing ? (
          <button
            type="button"
            aria-label={`Edit ${title}`}
            onClick={onEdit}
            className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-cerulean"
          >
            <Pencil className="size-3.5" />
          </button>
        ) : editing ? (
          <Badge variant="info">Editing</Badge>
        ) : null}
      </div>
      {children}
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-[12px] font-medium uppercase tracking-wide text-kw-muted">{label}</p>
      {children}
    </div>
  );
}

function TextBlock({ value, placeholder }: { value: string; placeholder: string }) {
  if (!value.trim()) return <p className="text-[13px] italic text-kw-muted">{placeholder}</p>;
  return <p className="whitespace-pre-wrap text-[13px] text-kw-shark">{value}</p>;
}

function BulletList({ items, placeholder }: { items: string[]; placeholder: string }) {
  const visible = items.filter((s) => s.trim() !== '');
  if (visible.length === 0) return <p className="text-[13px] italic text-kw-muted">{placeholder}</p>;
  return (
    <ul className="list-inside list-disc space-y-1.5">
      {visible.map((item, i) => (
        // Plain strings have no stable identity — index-composite keys are the best available.
        <li key={`${String(i)}-${item}`} className="text-[13px] text-kw-shark">
          {item}
        </li>
      ))}
    </ul>
  );
}

function StringListEditor({
  items,
  onChange,
  max,
  placeholder,
  ariaLabel,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  max: number;
  placeholder: string;
  ariaLabel: string;
}) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            aria-label={`${ariaLabel} ${String(i + 1)}`}
            value={item}
            maxLength={200}
            placeholder={placeholder}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            className={INPUT_CLS}
          />
          <button
            type="button"
            aria-label={`Remove ${ariaLabel.toLowerCase()} ${String(i + 1)}`}
            onClick={() => {
              onChange(items.filter((_, idx) => idx !== i));
            }}
            className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ))}
      {items.length < max ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            onChange([...items, '']);
          }}
        >
          <Plus className="size-3.5" />
          Add
        </Button>
      ) : (
        <p className="text-[11px] text-kw-muted">Maximum of {max} reached.</p>
      )}
    </div>
  );
}

/** Shared date/revenue/profit header row for the 3-year and 1-year sections. */
function PlanFields({
  editing,
  futureDate,
  revenue,
  profit,
  onChange,
}: {
  editing: boolean;
  futureDate: string;
  revenue: string;
  profit: string;
  onChange: (patch: { futureDate?: string; revenue?: string; profit?: string }) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Future date">
        {editing ? (
          <input
            type="date"
            aria-label="Future date"
            value={futureDate}
            onChange={(e) => {
              onChange({ futureDate: e.target.value });
            }}
            className={INPUT_CLS}
          />
        ) : (
          <TextBlock value={futureDate} placeholder="Pick a date" />
        )}
      </Field>
      <Field label="Revenue">
        {editing ? (
          <input
            aria-label="Revenue"
            value={revenue}
            maxLength={60}
            placeholder="e.g. $25M"
            onChange={(e) => {
              onChange({ revenue: e.target.value });
            }}
            className={INPUT_CLS}
          />
        ) : (
          <TextBlock value={revenue} placeholder="Revenue target" />
        )}
      </Field>
      <Field label="Profit">
        {editing ? (
          <input
            aria-label="Profit"
            value={profit}
            maxLength={60}
            placeholder="e.g. 15%"
            onChange={(e) => {
              onChange({ profit: e.target.value });
            }}
            className={INPUT_CLS}
          />
        ) : (
          <TextBlock value={profit} placeholder="Profit target" />
        )}
      </Field>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Save modal body
// ─────────────────────────────────────────────────────────────────────────

function SaveVtoForm({
  isSaving,
  error,
  onCancel,
  onSave,
}: {
  isSaving: boolean;
  error: unknown;
  onCancel: () => void;
  onSave: (changeSummary: string) => void;
}) {
  const [summary, setSummary] = useState('');
  return (
    <div className="space-y-4">
      <label className="flex flex-col gap-1">
        <span className="text-[13px] font-medium text-kw-label">Change summary (optional)</span>
        <textarea
          value={summary}
          maxLength={300}
          rows={2}
          placeholder="What changed and why? Shows in the version history."
          onChange={(e) => {
            setSummary(e.target.value);
          }}
          className={INPUT_CLS}
        />
      </label>

      {error ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
          <p className="text-[12px] text-kw-danger-text">
            {describeEosError(error, 'Could not save the V/TO.')}
          </p>
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          isLoading={isSaving}
          onClick={() => {
            onSave(summary);
          }}
        >
          Save new version
        </Button>
      </div>
    </div>
  );
}
